import type { LabDemosLocale } from '../types'

export const enLabDemos: LabDemosLocale = {
  eventDriven: {
    pendingLabel: 'Pending event',
    pendingHint: 'A real alarm landing on the queue. Fire it to watch the ontology synthesise a response.',
    fireButton: '▶ Fire event',
    refireButton: '↻ Re-fire',
    resetButton: 'Reset',
    goalSelectorLabel: 'Goal in effect',
    goalSelectorHint: 'Same alarm, different goal → completely different response path.',
    emptyStateTitle: 'Waiting for an event',
    emptyStateBody:
      'Press the fire button on the left. The ontology will read the event, infer affected entities, evaluate constraints, and propose an action — live.',
    activeEventLabel: 'Active event',
    insightTitle: 'What just happened',
    insightBody:
      'The path on the right was synthesised at fire-time — not pulled from a stored SOP. Switch the goal and watch the same alarm produce a different chain.',
    event: {
      code: 'ALARM K-3041',
      meta: '02:14 · CGS-3 · P-1023A',
      summary: 'High-sour feed pump · H₂S concentration spike',
      descriptor: 'H₂S 12.6% → 18.2%, Δ +44% in 47 s',
      payloadLabel: 'Payload',
      triggerNodeLabel: 'Event::ALARM_K-3041',
      triggerNodeDetail: 'Source PI tag CGS3.P1023A.H2S · received 02:14:07',
    },
    goals: [
      { id: 'compliance', label: 'Compliance-first' },
      { id: 'uptime', label: 'No-stop' },
      { id: 'cost', label: 'Min total cost' },
    ],
  },
  goalDriven: {
    wishesLabel: 'Pick a wish',
    wishesHint:
      'Each wish is just a human-readable goal. Selecting one tells the ontology to back-plan an execution path that satisfies every hard constraint.',
    insightTitle: 'Same ontology, different plan',
    insightBody:
      'Nothing about the graph below is stored. The same ontology is interrogated under a different intent each time you pick a wish — and produces an execution chain on the spot.',
    wishes: [
      {
        id: 'wish-compliance',
        goalId: 'compliance',
        quote: '"Find me a compliant mechanical seal for P-1023 with a 2-week ETA."',
        speaker: 'Procurement lead · M. Chen',
      },
      {
        id: 'wish-uptime',
        goalId: 'uptime',
        quote: '"Keep pump P-1023A running until the replacement arrives — bridge the gap."',
        speaker: 'Operations · Field engineer',
      },
      {
        id: 'wish-cost',
        goalId: 'cost',
        quote: '"Minimise total cost this quarter — including downtime and accident probability."',
        speaker: 'Finance · CFO office',
      },
    ],
  },
  explainability: {
    decisionsLabel: 'Past decisions',
    decisionsHint:
      'Pick a question an auditor might ask. The ontology expands the decision into the full chain that produced it.',
    emptyStateTitle: 'No decision selected',
    emptyStateBody:
      'Click a question on the left — every contributing node lights up; everything else dims out.',
    clearButton: 'Clear ✕',
    chainTitle: 'Causal chain',
    chainHint:
      'Each link is a separate entity in the ontology, clickable in production for further drill-down.',
    insightTitle: 'Auditable by construction',
    insightBody:
      'Every action the ontology recommends carries this chain as a by-product. The same chain can be replayed years later — provenance is built into the graph, not bolted on as logs.',
    decisions: [
      {
        id: 'decision-act-viol',
        question: '"Why was the action expandRfq + raiseMOC recommended?"',
        goalId: 'compliance',
        nodeId: 'g1-act',
      },
      {
        id: 'decision-c-plan',
        question: '"Why was Plan 53A banned for pump P-1023?"',
        goalId: 'compliance',
        nodeId: 'g1-c-plan',
      },
      {
        id: 'decision-c-mat',
        question: '"Why was SS316 banned for this service?"',
        goalId: 'compliance',
        nodeId: 'g1-c-mat',
      },
      {
        id: 'decision-cand-viol',
        question: '"Why did none of A/B/C make the shortlist?"',
        goalId: 'compliance',
        nodeId: 'g1-cand',
      },
      {
        id: 'decision-uptime-act',
        question: '"Why did we schedule intensified inspection on the standby?"',
        goalId: 'uptime',
        nodeId: 'g2-act',
      },
    ],
  },
  documentDriven: {
    documentsLabel: 'Sample documents',
    documentsHint:
      'Click any doc to "upload" it. Watch the LLM pull entities, the ontology bind them, and a handling chain synthesise itself.',
    extractingLabel: 'Extracting entities…',
    extractedLabel: 'Entities extracted · ready to bind',
    resetButton: '↻ Different doc',
    insightTitle: 'Different doc, different chain',
    insightBody:
      'The LLM does the unstructured-text extraction. The ontology constrains and grounds those entities so the synthesised path is type-correct. Same paradigm, very different chains depending on what fell in.',
    documents: [
      {
        id: 'contract',
        icon: '📄',
        name: 'SupplyContract-2025-Q2.pdf',
        meta: '14 pages · uploaded by Procurement',
        summary: 'Three-year supply agreement with Acme Pumps Inc.',
        entities: [
          { kind: 'Party', value: 'Acme Pumps Inc.' },
          { kind: 'Effective', value: '2025-04-01' },
          { kind: 'Cap', value: '$8.4 M / year' },
          { kind: 'Clause', value: 'Auto-renewal §12.3' },
          { kind: 'Risk', value: 'Force-majeure §17 (broad)' },
        ],
      },
      {
        id: 'rfq',
        icon: '📋',
        name: 'RFQ-29117-SEAL.xlsx',
        meta: '3 sheets · uploaded by Engineering',
        summary: 'Emergency mechanical-seal RFQ for pump P-1023.',
        entities: [
          { kind: 'Equipment', value: 'P-1023 (CGS-3)' },
          { kind: 'Bidders', value: 'A · B · C' },
          { kind: 'Required cert', value: 'NACE MR0175 current' },
          { kind: 'Lead time', value: '14 d · emergency SLA' },
          { kind: 'Budget cap', value: '$300 k' },
        ],
      },
      {
        id: 'incident',
        icon: '⚠',
        name: 'IncidentReport-K3041.md',
        meta: '6 sections · uploaded by Operations',
        summary: 'High-sour pump P-1023A seal Stage-1 leak overnight.',
        entities: [
          { kind: 'Asset', value: 'P-1023A' },
          { kind: 'Severity', value: 'Stage-1 leak · contained' },
          { kind: 'Detected', value: '02:14 local' },
          { kind: 'Hypothesis', value: 'Corrosion + H₂S spike' },
          { kind: 'Scope', value: 'CGS-3 Train A' },
        ],
      },
    ],
  },
  questionGrounded: {
    questionsLabel: 'Try a question',
    questionsHint:
      'Pick a question to see the ontology compose an answer along the graph. Click any [bracketed] entity to drill in.',
    emptyStateTitle: 'No question selected',
    emptyStateBody:
      'Pick a question on the left. The answer will cite every entity it walks through — clickable, all the way down to data sources.',
    entityDetailLabel: 'Entity detail',
    insightTitle: 'Grounded, not generated',
    insightBody:
      'Vanilla RAG returns a paragraph plus footnotes. This answer is built BY traversing the graph — every claim is an entity hop, every citation is a node id, and disagreements get logged back as proposed constraint changes.',
    questions: [
      {
        id: 'q-award-contract',
        question: 'Can I award contract X to Acme this week?',
        answer:
          'The contract is with [[entity:e1|Acme Pumps Inc.]]. Awarding requires VP signing per [[entity:e2|R-SIG-AUTH-01]] because the amount ($360 k) clears the [[entity:e3|$300 k threshold]]. Acme is [[entity:e4|approved at group AVL]] but [[entity:e5|suspended at field level since 2024-11]], so the award must be paired with an [[entity:e6|MOC waiver]] — otherwise the contract routes to legal for an override.',
        entities: [
          { id: 'e1', label: 'Acme Pumps Inc.', kind: 'object', detail: '4-year history · 12 prior contracts · default risk score 38/100' },
          { id: 'e2', label: 'R-SIG-AUTH-01', kind: 'constraint', detail: '≥ $300 k requires VP-level signature plus board notify if multi-year', citation: 'Group PA-2024-117 §3.1' },
          { id: 'e3', label: '$300 k threshold', kind: 'inference', detail: 'Currency-converted ceiling from group authority table; reviewed quarterly' },
          { id: 'e4', label: 'AVL · Group', kind: 'object', detail: 'Status: APPROVED since 2021-06; last audit 2024-08' },
          { id: 'e5', label: 'AVL · Field', kind: 'object', detail: 'Status: SUSPENDED 2024-11; reason: cert lapse, pending re-audit' },
          { id: 'e6', label: 'MOC waiver', kind: 'action', detail: '7-day group review · attached to contract record · audit-tracked' },
        ],
      },
      {
        id: 'q-pump-running',
        question: 'How long can pump P-1023A keep running?',
        answer:
          'Based on [[entity:e1|PI live data]] and the [[entity:e2|remaining-life inference]], P-1023A has [[entity:e3|9-12 days]] under current conditions. The [[entity:e4|R-BACKUP-SLA-11]] kicks in because this is shorter than the [[entity:e5|fastest compliant replacement ETA]] of 14 days, mandating [[entity:e6|intensified inspection plus a load limit]] in the meantime.',
        entities: [
          { id: 'e1', label: 'PI live data', kind: 'object', detail: 'Seal-face temp 92°C · vibration 3.2 mm/s · trend rising over 30 d' },
          { id: 'e2', label: 'remaining-life inference', kind: 'inference', detail: 'Corrosion model: H₂S 12.6% factor 1.32 · base 11 d → 8 d adjusted' },
          { id: 'e3', label: '9-12 days', kind: 'inference', detail: 'Range from low/high corrosion-factor envelope · re-evaluates hourly' },
          { id: 'e4', label: 'R-BACKUP-SLA-11', kind: 'constraint', detail: 'Standby life < fastest compliant ETA → mandatory intensified inspection + load limit', citation: 'Group EQ-BACKUP-SLA' },
          { id: 'e5', label: 'fastest compliant replacement ETA', kind: 'inference', detail: 'Computed across AVL · D supplier · 14 d emergency lead' },
          { id: 'e6', label: 'intensified inspection plus a load limit', kind: 'action', detail: 'Inspect every 4 h · 80% load cap · daily report to ops manager' },
        ],
      },
      {
        id: 'q-renewal-risk',
        question: 'Which accounts are at renewal risk?',
        answer:
          'Three accounts trigger [[entity:e1|R-RENEWAL-RISK-08]]: [[entity:e2|Account Theta]] (usage down 38% QoQ), [[entity:e3|Account Lambda]] (3 open support tickets > SLA), and [[entity:e4|Account Sigma]] (decision-maker departed 2025-02). The [[entity:e5|recommended outreach]] is a CSM touch within 7 days for Theta and Lambda; Sigma routes to sales for stakeholder remapping.',
        entities: [
          { id: 'e1', label: 'R-RENEWAL-RISK-08', kind: 'constraint', detail: 'Composite rule: usage drop + support-ticket breach + champion turnover', citation: 'CS Playbook §4' },
          { id: 'e2', label: 'Account Theta', kind: 'object', detail: 'MRR $45 k · renewal 2025-Q3 · usage down 38% over 90 d' },
          { id: 'e3', label: 'Account Lambda', kind: 'object', detail: 'MRR $62 k · renewal 2025-Q2 · 3 tickets aged > 14 d' },
          { id: 'e4', label: 'Account Sigma', kind: 'object', detail: 'MRR $118 k · renewal 2025-Q3 · primary champion departed 2025-02-14' },
          { id: 'e5', label: 'recommended outreach', kind: 'action', detail: 'Touch cadence + content recipe + meeting cadence per account tier' },
        ],
      },
    ],
  },
  changePropagation: {
    controlLabel: 'NACE H₂S threshold (sour service)',
    controlHint:
      'Drag the proposed threshold. The ontology re-evaluates every asset live and flips status badges where the rule fires differently.',
    thresholdLabel: 'Threshold',
    currentLabel: 'Current',
    proposedLabel: 'Proposed',
    unitLabel: 'ppm',
    resetButton: 'Reset to current',
    impactTitle: 'Impact across the fleet',
    impactSummaryTpl: '{affected} of {total} assets flipped status',
    statusLabels: {
      standard: 'Standard',
      watch: 'Watch',
      sour: 'Sour service',
    },
    flipBadge: 'Changed',
    assetMetaLabel: 'H₂S reading',
    insightTitle: 'One rule edit, full-graph propagation',
    insightBody:
      'Editing the threshold once flips status on every affected asset, queues MOC review, and tags in-flight RFQs that mention the old limit. With SOP-based systems each Skill that references the rule has to be edited separately — and the audit trail is a forensics project.',
    assets: [
      { id: 'a-p1023', code: 'P-1023', name: 'Feed pump · CGS-3 train A', h2sPpm: 12600 },
      { id: 'a-p1024', code: 'P-1024', name: 'Feed pump · CGS-3 train B', h2sPpm: 8500 },
      { id: 'a-v1010', code: 'V-1010', name: 'Acid-gas knockout', h2sPpm: 3200 },
      { id: 'a-p1070', code: 'P-1070', name: 'Produced-water pump', h2sPpm: 1100 },
      { id: 'a-v1040', code: 'V-1040', name: 'Stabiliser feed drum', h2sPpm: 780 },
      { id: 'a-p1031', code: 'P-1031', name: 'Crude transfer pump', h2sPpm: 420 },
      { id: 'a-p1050', code: 'P-1050', name: 'Glycol circulation', h2sPpm: 220 },
      { id: 'a-v1020', code: 'V-1020', name: 'Sweet-side scrubber', h2sPpm: 180 },
      { id: 'a-v1060', code: 'V-1060', name: 'Wash-water vessel', h2sPpm: 80 },
      { id: 'a-p1032', code: 'P-1032', name: 'Diesel transfer pump', h2sPpm: 35 },
    ],
    currentThresholdPpm: 500,
    thresholdRange: { min: 50, max: 2000, step: 10 },
  },
  riskScoring: {
    controlLabel: 'Score Acme Pumps Inc.',
    controlHint:
      'Set each dimension (0 = best · 10 = worst). The ontology aggregates weighted contributions into a single supplier-risk score.',
    resetButton: 'Reset to baseline',
    dimensionsTitle: 'Risk dimensions',
    dimensions: [
      { id: 'financial', label: 'Financial health', weightPct: 40, description: 'Cash position, leverage, recent earnings trend', defaultValue: 6 },
      { id: 'operational', label: 'Operational reliability', weightPct: 35, description: 'On-time delivery, defect rate, quality complaints', defaultValue: 4 },
      { id: 'esg', label: 'ESG & compliance', weightPct: 25, description: 'Safety record, sanctions list, audit findings', defaultValue: 5 },
    ],
    scoreTitle: 'Supplier risk score',
    scoreCaption: '0 = safe · 100 = critical',
    thresholds: {
      safe: { upper: 30, label: 'Safe to award' },
      watch: { upper: 60, label: 'Watch — flag for review' },
      critical: { label: 'Critical — block until remediated' },
    },
    attributionTitle: 'How the score breaks down',
    attributionHint:
      'Each dimension contributes value × weight. Move any slider — the score and every contribution recompute instantly.',
    contributionFormula: '{value} × {weight}% = {contribution}',
    insightTitle: 'A score that explains itself',
    insightBody:
      'Move any slider; everything recomputes. The numbers are never opaque — each contribution links back to the rule and data sources behind it. Click any one to drill in.',
    contextSupplier: {
      label: 'Supplier under review',
      name: 'Acme Pumps Inc.',
      detail: '4-year history · 12 prior contracts · last audit 2024-08',
    },
  },
  whatIf: {
    scenariosLabel: 'Hypothetical',
    scenariosHint:
      'Pick a future trajectory. The ontology projects forward month by month — each asset gets re-classified at every step. Watch for when constraints fire.',
    horizonLabel: 'Horizon',
    monthsUnit: 'months',
    resetButton: 'Reset',
    legendLabel: 'Legend',
    monthAxisLabel: 'Month',
    insightTitle: 'Time-aware, not instant',
    insightBody:
      'The ontology re-evaluates the fleet at every projected step, not just "now". This is how you spot constraints that will fire next quarter, not just constraints that fire today.',
    scenarios: [
      {
        id: 'h2s-30',
        title: 'H₂S +30% over 6 months',
        description: 'A gradual sour-shift across the field — 5% growth per month',
        h2sGrowthPctPerMonth: 5,
      },
      {
        id: 'h2s-80',
        title: 'Sharp sour shift · +80% / 6 months',
        description: 'A reservoir change pushes H₂S up 13% per month',
        h2sGrowthPctPerMonth: 13,
      },
      {
        id: 'h2s-stable',
        title: 'Conditions stable',
        description: '0.5% drift — operating noise, no real change',
        h2sGrowthPctPerMonth: 0.5,
      },
    ],
    horizonRange: { min: 3, max: 18, step: 1, default: 9 },
    baseThresholdPpm: 500,
    assets: [
      { id: 'a-p1023', code: 'P-1023', name: 'Feed pump · CGS-3 A', h2sPpmStart: 12600 },
      { id: 'a-p1024', code: 'P-1024', name: 'Feed pump · CGS-3 B', h2sPpmStart: 8500 },
      { id: 'a-v1010', code: 'V-1010', name: 'Acid-gas knockout', h2sPpmStart: 3200 },
      { id: 'a-p1070', code: 'P-1070', name: 'Produced-water pump', h2sPpmStart: 600 },
      { id: 'a-v1040', code: 'V-1040', name: 'Stabiliser feed drum', h2sPpmStart: 380 },
      { id: 'a-p1031', code: 'P-1031', name: 'Crude transfer pump', h2sPpmStart: 220 },
      { id: 'a-p1050', code: 'P-1050', name: 'Glycol circulation', h2sPpmStart: 160 },
      { id: 'a-v1020', code: 'V-1020', name: 'Sweet-side scrubber', h2sPpmStart: 90 },
    ],
    statusLabels: {
      standard: 'Standard',
      watch: 'Watch',
      sour: 'Sour',
    },
    firstFlipTpl: 'First flip @ M{month}: {from} → {to}',
    stableLabel: 'Stable',
  },
  continuousAudit: {
    controlLabel: 'Nightly fleet audit',
    controlHint:
      'A scheduled pass over every asset, contract, and rule. The ontology re-evaluates and lists every drift or silently-broken constraint.',
    runButton: '▶ Run audit',
    runningLabel: 'Scanning…',
    resetButton: 'Reset',
    progressTpl: '{scanned} / {total} assets scanned',
    summaryTitle: 'Findings',
    summaryCountTpl: '{count} findings',
    bySeverityTpl: '{critical} critical · {warn} watch · {info} info',
    severityLabels: {
      critical: 'Critical',
      warn: 'Watch',
      info: 'Info',
    },
    draftFixButton: 'Draft fix →',
    emptyStateTitle: 'Audit not yet run',
    emptyStateBody: 'Press the run button. Findings stream in as the audit walks through each asset.',
    insightTitle: 'Aggregated, not alerted',
    insightBody:
      'Different from event-driven response: this runs continuously and aggregates into one dashboard, not a flood of independent alerts. Each finding ships with a draft fix already attached.',
    findings: [
      {
        id: 'f1',
        asset: 'P-1023A',
        rule: 'R-CERT-CURRENT-02',
        severity: 'critical',
        title: 'NACE certificate outdated',
        detail: 'Asset cites NACE 2024-08; current standard is 2025-03. Sour service deviation triggered.',
        fixDraft: 'Issue cert-renewal request + tag the in-flight RFQ as blocked',
      },
      {
        id: 'f2',
        asset: 'V-1040',
        rule: 'R-DRIFT-H2S-04',
        severity: 'critical',
        title: 'H₂S reading drift +12% over 30 d',
        detail: 'Sustained 12% rise crosses the watch band — pump approaches sour classification.',
        fixDraft: 'Schedule MOC review + invite alternate suppliers for material upgrade',
      },
      {
        id: 'f3',
        asset: 'Supplier B',
        rule: 'R-AVL-AUDIT-09',
        severity: 'warn',
        title: 'AVL re-audit window passed',
        detail: 'Field AVL audit overdue by 14 d. Suspended status pending re-audit.',
        fixDraft: 'Auto-schedule audit visit + freeze new RFQs to this supplier',
      },
      {
        id: 'f4',
        asset: 'RFQ 29133',
        rule: 'R-RFQ-STALE-07',
        severity: 'warn',
        title: 'RFQ references old NACE threshold',
        detail: 'RFQ-29133 spec cites the pre-2025-03 NACE limit. Bids may be evaluated against outdated rule.',
        fixDraft: 'Notify procurement to re-issue RFQ with current standard',
      },
      {
        id: 'f5',
        asset: 'P-1031',
        rule: 'R-VIB-TREND-12',
        severity: 'warn',
        title: 'Vibration trend exceeds slope band',
        detail: '90-day vibration slope 0.04 mm/s/d — above 0.025 mm/s/d threshold for 28 d.',
        fixDraft: 'Schedule predictive-maintenance inspection · attach to next planned shutdown',
      },
      {
        id: 'f6',
        asset: 'Group MOC log',
        rule: 'R-MOC-CLOSURE-15',
        severity: 'info',
        title: 'MOC item open > SLA',
        detail: 'MOC-2025-074 open 23 d; SLA is 14 d.',
        fixDraft: 'Escalate to MOC chair + auto-extend with stakeholder ack',
      },
      {
        id: 'f7',
        asset: 'Contract C-118',
        rule: 'R-CONTRACT-RENEWAL-03',
        severity: 'info',
        title: 'Contract auto-renews in 45 d',
        detail: 'Annual cap $8.4M auto-renews unless cancelled by 2025-07-30.',
        fixDraft: 'Route to procurement for renewal review · attach updated risk score',
      },
      {
        id: 'f8',
        asset: 'V-1020',
        rule: 'R-CONS-RECON-02',
        severity: 'info',
        title: 'Cross-system reading mismatch',
        detail: 'PI reads 90 ppm; lab sample reads 105 ppm. > 10% deviation.',
        fixDraft: 'Schedule reconciliation + flag sensor calibration window',
      },
    ],
  },
  multiAgent: {
    incidentLabel: 'Incoming incident',
    incidentTitle: 'INCIDENT-2025-K3041 · P-1023A Stage-1 leak',
    incidentDetail: 'Detected 02:14 local · CGS-3 train A · sour-service feed pump · no HSE exposure',
    injectButton: '▶ Inject into workspace',
    injectedLabel: 'Workspace activity',
    resetButton: 'Reset',
    timelineLabel: 'Agent timeline',
    workspaceLabel: 'Shared ontology workspace',
    emptyStateTitle: 'No incident yet',
    emptyStateBody:
      'Click inject. Each agent reads the latest graph state, posts its contribution, and the next agent reacts. No point-to-point messages — just the shared ontology.',
    insightTitle: 'Shared graph, not point-to-point',
    insightBody:
      'Each agent reads the latest graph state, posts its contribution, the next agent reacts. New agents plug in by declaring their input / output node types — no protocol negotiation.',
    agents: [
      { id: 'triage', icon: '🔍', name: 'Triage agent', accent: 'amber' },
      { id: 'diagnosis', icon: '🩺', name: 'Diagnosis agent', accent: 'cyan' },
      { id: 'fix', icon: '🔧', name: 'Fix-suggester', accent: 'violet' },
      { id: 'human', icon: '👤', name: 'Human in the loop', accent: 'pass' },
    ],
    timeline: [
      {
        id: 'ma-1',
        timestamp: '02:14:07',
        agentId: 'triage',
        verb: 'Posted',
        nodeId: 'ma-asset',
        nodeKind: 'object',
        nodeLabel: 'Asset::P-1023A',
        nodeDetail: 'Sour-service feed pump · CGS-3 train A · severity=Stage-1',
      },
      {
        id: 'ma-2',
        timestamp: '02:14:09',
        agentId: 'triage',
        verb: 'Posted',
        nodeId: 'ma-event',
        nodeKind: 'inference',
        nodeLabel: 'Event::Stage-1 leak',
        nodeDetail: 'PI tag CGS3.P1023A.LEAK = 1 · contained · no HSE exposure',
        parents: ['ma-asset'],
      },
      {
        id: 'ma-3',
        timestamp: '02:14:11',
        agentId: 'diagnosis',
        verb: 'Hypothesised',
        nodeId: 'ma-h1',
        nodeKind: 'inference',
        nodeLabel: 'Hypothesis::corrosion',
        nodeDetail: 'Probability 0.72 · H₂S spike 12.6% → 18.2% over 30 d',
        parents: ['ma-event'],
      },
      {
        id: 'ma-4',
        timestamp: '02:14:12',
        agentId: 'diagnosis',
        verb: 'Hypothesised',
        nodeId: 'ma-h2',
        nodeKind: 'inference',
        nodeLabel: 'Hypothesis::seal-wear',
        nodeDetail: 'Probability 0.18 · last overhaul 2024-09 · within service curve',
        parents: ['ma-event'],
      },
      {
        id: 'ma-5',
        timestamp: '02:14:14',
        agentId: 'fix',
        verb: 'Posted',
        nodeId: 'ma-c-moc',
        nodeKind: 'constraint',
        nodeLabel: 'R-MOC-INCIDENT-02',
        nodeDetail: 'Stage-1 leak on sour service → MOC required within 48 h',
        parents: ['ma-event'],
      },
      {
        id: 'ma-6',
        timestamp: '02:14:16',
        agentId: 'fix',
        verb: 'Proposed',
        nodeId: 'ma-plan',
        nodeKind: 'candidate',
        nodeLabel: 'Plan::expediteRfq',
        nodeDetail: 'Invite D supplier · 14-day ETA · pair with intensified inspection',
        parents: ['ma-h1', 'ma-c-moc'],
      },
      {
        id: 'ma-7',
        timestamp: '02:14:19',
        agentId: 'human',
        verb: 'Approved',
        nodeId: 'ma-act',
        nodeKind: 'action',
        nodeLabel: 'Action::expediteRfq + raiseMOC',
        nodeDetail: 'Signed off by ops manager · MOC chair notified · daily-report ON',
        parents: ['ma-plan'],
      },
    ],
  },
}
